import { useCallback, useEffect, useRef, useState } from 'react';

import config from '@/config';
import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import RankingIcon from '@/shared-components/icons/components/navigation/RankingIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import { getColorMatchPercentage } from '@/utils/colorMatch.utils';
import {
  mixComponentsToRgb,
  rgbToCss,
} from '@/utils/gameMixColor.utils';
import { getSsrArtGamesBootstrap } from '@/utils/ssrArtGamesBootstrap';

import ArtGameHeader from '../components/ArtGameHeader';
import RadioControl from '../radio/RadioControl';

import {
  createRoundPayload,
  evaluateRound,
  fetchLeaderboard,
  getRoundThreshold,
  submitRoundResult,
  useColorMixLeaderboard,
} from './leaderboard';
import LeaderboardPanel from './LeaderboardPanel';
import SharePopup from './SharePopup';
import { BASE_COLORS, COLOR_KEYS, EMPTY_COLOR, generateTargetColor, TARGET_PROFILE_COUNT } from './targetColor';

import './index.css';

const POUR_LIMIT = 24;

const createEmptyMix = () => COLOR_KEYS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});

const gradeKeyFromPercentage = (percentage) => {
  if (percentage >= 97) {
    return 'flawless_match';
  }

  if (percentage >= 93) {
    return 'gallery_ready';
  }

  if (percentage >= 88) {
    return 'great_eye';
  }

  if (percentage >= 75) {
    return 'getting_close';
  }

  return 'keep_exploring';
};

function readInitialColorMixSeoPayload() {
  const payload = getSsrArtGamesBootstrap('color-mix');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

export default function ColorMixGame() {
  const t = useTranslations('common.newspaper.art_games.color_mix');
  const leaderboardT = useTranslations('common.newspaper.art_games.color_mix.leaderboard');
  const goBackHandler = useGoBack('/1/newspaper/art-games');
  const { state } = useStateHandler();
  const sessionUsername = state?.user_session?.user_username;
  const [round, setRound] = useState(1);
  const [profileOffset] = useState(() => Math.floor(Math.random() * TARGET_PROFILE_COUNT));
  const [targetColor, setTargetColor] = useState(() => generateTargetColor({ round: 1, profileOffset }));
  const mixComponentsRef = useRef(createEmptyMix());
  const [mixColor, setMixColor] = useState(EMPTY_COLOR);
  const [poursUsed, setPoursUsed] = useState(0);
  const poursRef = useRef(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [totalPours, setTotalPours] = useState(0);
  const [resultCard, setResultCard] = useState(null);
  const resultHeadingRef = useRef(null);
  const roundSummaryRef = useRef(null);
  const focusRoundSummaryRef = useRef(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [isSavingRound, setIsSavingRound] = useState(false);
  const [bestScore, setBestScore] = useState(0);
  // best % inside the current run (resets on miss)
  const [sessionBestScore, setSessionBestScore] = useState(0);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null);
  const leaderboardData = useColorMixLeaderboard();
  const [isLeaderboardPopupOpen, setLeaderboardPopupOpen] = useState(false);
  const [colorMixSeo, setColorMixSeo] = useState(() => readInitialColorMixSeoPayload());

  useEffect(() => {
    let ignore = false;

    async function loadColorMixSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/art-games/color-mix`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setColorMixSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load Color Mix SEO payload', error);
        }
      }
    }

    loadColorMixSeo();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadPersistedStats = async () => {
      try {
        const leaderboard = await fetchLeaderboard();
        if (!isMounted) {
          return;
        }

        const userStats = leaderboard?.user;
        if (!userStats) {
          return;
        }

        if (Number.isFinite(userStats.bestStreak)) {
          setBestStreak((previousBest) => Math.max(previousBest, userStats.bestStreak));
        } else if (Number.isFinite(userStats.streak)) {
          setBestStreak((previousBest) => Math.max(previousBest, userStats.streak));
        }
      } catch (_error) {
        // Silently ignore leaderboard load failures; the UI will fall back to local state.
      }
    };

    loadPersistedStats();

    return () => {
      isMounted = false;
    };
  }, [fetchLeaderboard]);

  const addColor = useCallback((colorKey) => {
    if (poursRef.current >= POUR_LIMIT) return;

    const updated = {
      ...mixComponentsRef.current,
      [colorKey]: mixComponentsRef.current[colorKey] + 1,
    };

    mixComponentsRef.current = updated;
    poursRef.current += 1;
    setPoursUsed(poursRef.current);
    setMixColor(mixComponentsToRgb(updated, BASE_COLORS, EMPTY_COLOR));
  }, []);

  const resetMix = useCallback(() => {
    mixComponentsRef.current = createEmptyMix();
    setMixColor(EMPTY_COLOR);
    poursRef.current = 0;
    setPoursUsed(0);
  }, []);

  // Reset the entire "run" after a miss: round+streak+points+pours restart
  const resetRun = useCallback((previousColor) => {
    setStreak(0);
    setRound(1);
    setTargetColor(generateTargetColor({ round: 1, previousColor, profileOffset }));
    setTotalPoints(0);
    setTotalPours(0);
    setSessionBestScore(0);
    resetMix();
  }, [profileOffset, resetMix]);

  const handleAcceptMix = useCallback(() => {
    if (poursRef.current <= 0 || resultCard || isSavingRound) return;

    const poursForRound = poursRef.current;
    const closeness = getColorMatchPercentage(mixColor, targetColor);
    const gradeKey = gradeKeyFromPercentage(closeness);

    const evaluation = evaluateRound({
      match: closeness,
      poursUsed: poursForRound,
      round,
      streak,
      totalPoints,
      totalPours,
    });

    const nextResult = {
      closeness: evaluation.clampedMatch,
      gradeKey,
      pours: poursForRound,
      threshold: evaluation.threshold,
      roundPoints: evaluation.roundPoints,
      isCorrect: evaluation.isCorrect,
    };

    // Track best % inside the current session only
    const nextSessionBest = Math.max(sessionBestScore, evaluation.clampedMatch);
    setSessionBestScore(nextSessionBest);

    setResultCard(nextResult);
    setSubmissionError(null);

    if (evaluation.isCorrect) {
      const nextStreak = evaluation.nextStreak;
      setStreak(nextStreak);

      if (nextStreak > bestStreak) {
        setBestScore((prev) => Math.max(prev, nextSessionBest));
      }
      setBestStreak((prevBest) => Math.max(prevBest, nextStreak));

      // Keep accumulating inside the run
      setTotalPoints(evaluation.updatedTotalPoints);
      setTotalPours(evaluation.updatedTotalPours);
    } else {
      // On miss: if this run ties your best streak, allow bestScore to improve
      if (streak === bestStreak) {
        setBestScore((prev) => Math.max(prev, nextSessionBest));
      }
    }

    // Save the submitted round before allowing the next one.
    const payload = createRoundPayload({
      match: evaluation.clampedMatch,
      poursUsed: poursForRound,
      round,
      userUsername: sessionUsername,
      roundId:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `round-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    });

    if (payload) {
      setIsSavingRound(true);
      submitRoundResult(payload, { emitUpdateEvent: !evaluation.isCorrect })
        .then((submission) => {
          const stats = submission?.data?.stats ?? submission?.stats;
          if (!stats) return;
          if (Number.isFinite(stats.bestStreak)) {
            setBestStreak((previousBest) => Math.max(previousBest, stats.bestStreak));
          } else if (Number.isFinite(stats.streak)) {
            setBestStreak((previousBest) => Math.max(previousBest, stats.streak));
          }
        })
        .catch((error) => {
          setSubmissionError(error);
        })
        .finally(() => {
          setIsSavingRound(false);
        });
    }
  }, [
    mixColor,
    resultCard,
    isSavingRound,
    round,
    streak,
    bestStreak,
    targetColor,
    totalPoints,
    totalPours,
    sessionBestScore,
    sessionUsername,
  ]);

  const handleNextRound = useCallback(() => {
    if (!resultCard || isSavingRound) return;

    if (resultCard.isCorrect) {
      const nextRound = round + 1;
      setRound(nextRound);
      setTargetColor(generateTargetColor({ round: nextRound, previousColor: targetColor, profileOffset }));
      resetMix();
    } else {
      resetRun(targetColor);
    }

    focusRoundSummaryRef.current = true;
    setResultCard(null);
    setSubmissionError(null);
  }, [resultCard, isSavingRound, round, targetColor, profileOffset, resetMix, resetRun]);

  const openSharePopup = useCallback(() => {
    setShareFeedback(null);
    setIsShareOpen(true);
  }, []);

  const closeSharePopup = useCallback(() => {
    setIsShareOpen(false);
    setShareFeedback(null);
    setIsSharing(false);
  }, []);

  const closeLeaderboardPopup = useCallback(() => {
    setLeaderboardPopupOpen(false);
  }, []);

  const openLeaderboardPopup = useCallback(() => {
    setLeaderboardPopupOpen(true);
  }, []);

  const handleShareInvite = useCallback(async () => {
    if (isSharing) {
      return;
    }

    const shareUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/newspaper/art-games/color-mix`
      : 'https://suarte.art/newspaper/art-games/color-mix';

    const hasScore = bestScore > 0;
    const formattedScore = hasScore ? `${bestScore.toFixed(1)}%` : null;
    const shareText = hasScore
      ? t('share.score_text', { score: formattedScore })
      : t('share.default_text');

    const payload = {
      title: t('share.title'),
      text: shareText,
      url: shareUrl,
    };

    setIsSharing(true);
    setShareFeedback(null);

    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share(payload);
        setShareFeedback({ tone: 'success', message: t('share.feedback.invite_sent') });
      } else if (
        typeof navigator !== 'undefined'
        && navigator.clipboard
        && typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(`${payload.text} ${payload.url}`);
        setShareFeedback({ tone: 'success', message: t('share.feedback.link_copied') });
      } else {
        setShareFeedback({ tone: 'info', message: t('share.feedback.manual_share'), link: shareUrl });
      }
    } catch (_shareError) {
      if (
        typeof navigator !== 'undefined'
        && navigator.clipboard
        && typeof navigator.clipboard.writeText === 'function'
      ) {
        try {
          await navigator.clipboard.writeText(`${payload.text} ${payload.url}`);
          setShareFeedback({ tone: 'success', message: t('share.feedback.copied_fallback') });
        } catch (_clipboardError) {
          setShareFeedback({ tone: 'error', message: t('share.feedback.auto_share_failed'), link: shareUrl });
        }
      } else {
        setShareFeedback({ tone: 'error', message: t('share.feedback.open_share_failed'), link: shareUrl });
      }
    } finally {
      setIsSharing(false);
    }
  }, [bestScore, isSharing, t]);

  const poursRemaining = POUR_LIMIT - poursUsed;
  const poursProgress = poursRemaining / POUR_LIMIT;
  const passThreshold = resultCard?.threshold ?? getRoundThreshold(round);

  useEffect(() => {
    if (resultCard) {
      resultHeadingRef.current?.focus();
    } else if (focusRoundSummaryRef.current) {
      roundSummaryRef.current?.focus();
      focusRoundSummaryRef.current = false;
    }
  }, [resultCard]);

  return (
    <>
      <StaticSeoHead
        title={colorMixSeo?.title || t('meta.title')}
        description={colorMixSeo?.metaDescription || t('meta.description')}
        canonicalUrl={colorMixSeo?.canonicalUrl}
        canonicalPath={colorMixSeo?.canonicalPath || '/newspaper/art-games/color-mix'}
        robots={colorMixSeo?.robots}
        ogType={colorMixSeo?.ogType}
        image={colorMixSeo?.ogImage}
        pageType={colorMixSeo?.pageType || 'WebApplication'}
        structuredData={colorMixSeo?.structuredData}
      />
      <div className="color-mix-page">
        <ArtGameHeader
          className="color-mix__header"
          titleClassName="color-mix__title"
          title={t('hero.title')}
          onBack={goBackHandler}
          rightSlot={
            <>
              <RadioControl />
              <button
                type="button"
                className="color-mix__leaderboard-button"
                onClick={openLeaderboardPopup}
                aria-label={leaderboardT('title')}
              >
                <span className="color-mix__leaderboard-button-label">{leaderboardT('title')}</span>
                <RankingIcon className="color-mix__leaderboard-button-icon" />
              </button>
            </>
          }
          onShare={openSharePopup}
          shareAriaLabel={t('hero.share_aria')}
        />

        <div className="color-mix__workspace">
          <main className="color-mix__content">
            <div className="color-mix__round-summary" ref={roundSummaryRef} tabIndex={-1}>
              <span>{t('stats.round_number', { value: round })}</span>
              <span>{t('stats.pass_threshold', { value: passThreshold })}</span>
            </div>

            <div className="color-mix__comparison">
              <section className="color-mix__target" aria-labelledby="color-mix-target-label">
                <h2 id="color-mix-target-label" className="color-mix__swatch-label">{t('result.target_label')}</h2>
                <div className="color-mix__swatch" style={{ background: rgbToCss(targetColor) }} />
              </section>
              <section className="color-mix__bin" aria-labelledby="color-mix-bin-label">
                <h2 id="color-mix-bin-label" className="color-mix__swatch-label">{t('result.mix_label')}</h2>
                <div className="color-mix__swatch" style={{ background: rgbToCss(mixColor) }} />
              </section>
            </div>

            {resultCard ? (
              <section className={`color-mix__result ${resultCard.isCorrect ? 'color-mix__result--success' : 'color-mix__result--miss'}`} aria-labelledby="color-mix-result-title">
                <h2 id="color-mix-result-title" ref={resultHeadingRef} tabIndex={-1} className="color-mix__result-title">
                  {t(resultCard.isCorrect ? 'result.passed' : 'result.run_ended')}
                </h2>
                <p className="color-mix__result-grade">{t(`result.grades.${resultCard.gradeKey}`)}</p>
                <p className="color-mix__result-score">{t('result.match', { score: resultCard.closeness.toFixed(1) })}</p>
                <p className="color-mix__result-threshold">
                  {t('result.threshold', { threshold: resultCard.threshold, points: resultCard.roundPoints })}
                </p>
                <span className="color-mix__result-footnote">{t('result.pours_used', { value: resultCard.pours })}</span>
                <ContinueButton type="button" className="color-mix__primary color-mix__next" onClick={handleNextRound} disabled={isSavingRound}>
                  {isSavingRound ? t('controls.saving') : t(resultCard.isCorrect ? 'controls.next_round' : 'controls.start_new_run')}
                </ContinueButton>
              </section>
            ) : (
              <section className="color-mix__controls" aria-label={t('aria.pour_colors')}>
                <div className="color-mix__pour-status">
                  <strong>{t('stats.pours_left', { value: poursRemaining })}</strong>
                  <div
                    className="color-mix__progress"
                    role="progressbar"
                    aria-label={t('stats.pours_remaining')}
                    aria-valuemin={0}
                    aria-valuemax={POUR_LIMIT}
                    aria-valuenow={poursRemaining}
                  >
                    <div className="color-mix__progress-bar" style={{ width: `${poursProgress * 100}%` }} />
                  </div>
                </div>
                <div className="color-mix__buttons">
                  {COLOR_KEYS.map((colorKey) => {
                    const baseColor = BASE_COLORS[colorKey];
                    const isDisabled = poursRef.current >= POUR_LIMIT;
                    return (
                      <button
                        type="button"
                        key={colorKey}
                        className={`color-mix__button ${colorKey}`}
                        aria-label={t('controls.pour_color', { color: t(baseColor.labelKey) })}
                        onClick={() => addColor(colorKey)}
                        disabled={isDisabled}
                      >
                        <span className="color-mix__button-outer">
                          <span className="color-mix__button-shadow" />
                          <span className="color-mix__button-inner" />
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="color-mix__instruction">{t('controls.tap_hint')}</p>
                <div className="color-mix__actions">
                  <ContinueButton type="button" className="color-mix__secondary" onClick={resetMix} disabled={poursUsed <= 0}>
                    {t('controls.reset')}
                  </ContinueButton>
                  <ContinueButton type="button" className="color-mix__primary" onClick={handleAcceptMix} disabled={poursUsed <= 0 || isSavingRound}>
                    {t('controls.check_mix')}
                  </ContinueButton>
                </div>
              </section>
            )}
            {submissionError ? (
              <p role="alert" className="color-mix__feedback color-mix__feedback--error">
                {t('errors.round_save_failed', { reason: submissionError.message })}
              </p>
            ) : null}
          </main>
          <aside className="color-mix__status" aria-label={t('aria.session_stats')}>
            <div className="color-mix__stat">
              <span className="color-mix__stat-label">{t('stats.points')}</span>
              <span className="color-mix__stat-value">{totalPoints}</span>
            </div>
            <div className="color-mix__stat">
              <span className="color-mix__stat-label">{t('stats.current_streak')}</span>
              <span className="color-mix__stat-value">{streak}</span>
            </div>
            <div className="color-mix__stat">
              <span className="color-mix__stat-label">{t('stats.best')}</span>
              <span className="color-mix__stat-value">{bestStreak}</span>
            </div>
            <div className="color-mix__stat">
              <span className="color-mix__stat-label">{t('stats.total_pours')}</span>
              <span className="color-mix__stat-value">{totalPours}</span>
            </div>
          </aside>
        </div>
      </div>
      <SharePopup
        open={isShareOpen}
        onClose={closeSharePopup}
        highestScore={bestScore}
        onShare={handleShareInvite}
        shareFeedback={shareFeedback}
        isSharing={isSharing}
      />
      <GenericPopup
        open={isLeaderboardPopupOpen}
        onCloseAction={closeLeaderboardPopup}
        className="color-mix__leaderboard-popup"
        opacity
      >
        <LeaderboardPanel {...leaderboardData} onClose={closeLeaderboardPopup} />
      </GenericPopup>
    </>
  );
}
